import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';
import './resolutionModal.scss';
import download from 'assets/download.svg';
import infoIcon from './../../../../assets/images/action/ic-info-blue.svg';
import axios from 'axios';
import { get_service_endpoint } from 'ServiceEndpoints';

const DownloadPill = ({ pillName, link, containerStyle = '' }) => {
  return (
    <div>
      {link ? (
        <a href={link} target="_blank" rel="noreferrer">
          <div className="pill-container containerStyle">
            <span className="pill-name">{pillName}</span>
            <img className="pill-icon" src={download} alt="download" />
          </div>
        </a>
      ) : (
        <div>--</div>
      )}
    </div>
  );
};

const DataRow = ({
  dataLabel = null,
  dataVal = null,
  isDoc = null,
  docUrl = undefined,
  docName = null,
  isPrice = false,
}) => {
  return (
    <div className="data-row-container">
      <span className="data-row-label">{dataLabel}</span>
      {dataVal ? (
        <span className="data-row-value">
          {isPrice ? '₹ ' : null}
          {dataVal}
        </span>
      ) : null}
      {isDoc ? (
        <DownloadPill containerStyle="pill-style" pillName={`${docName}`} link={docUrl} />
      ) : null}
    </div>
  );
};

const supplierDocType = {
  1: 'Credit note',
  2: 'Debit note',
};
const creditNoteLabelMapping = [
  ['customerCreditNoteNumber', 'Credit note number'],
  ['customerCreditNoteDate', 'Credit note date'],
  ['customerCreditNoteAmount', 'Credit note amount'],
  ['customerCreditNoteUrl', 'Credit note'],
];
const refundLabelMapping = [
  ['refundMode', 'Mode of payment'],
  ['refundDate', 'Refund date'],
  ['refundAmount', 'Refund amount'],
  ['utrRefNumber', 'UTR / Reference number'],
  ['refundDocumentUrl', 'Supporting document'],
];

const getSupplierDocLabelMapping = (supplierDocDetails) => {
  const supplierDocLabelMapping = [
    ['supplierDocumentType', 'Document Type'],
    [
      'supplierDocumentNumber',
      `${supplierDocType[supplierDocDetails.supplierDocumentType]} number`,
    ],
    ['supplierDocumentDate', `${supplierDocType[supplierDocDetails.supplierDocumentType]} date`],
    [
      'supplierDocumentAmount',
      `${supplierDocType[supplierDocDetails.supplierDocumentType]} amount`,
    ],
    ['supplierDocumentUrl', `${supplierDocType[supplierDocDetails.supplierDocumentType]}`],
  ];
  return supplierDocLabelMapping;
};

const priceVariables = {
  refundAmount: true,
  customerCreditNoteAmount: true,
  supplierDocumentAmount: true,
};

const dateVariables = {
  refundDate: true,
  customerCreditNoteDate: true,
  supplierDocumentDate: true,
};

const formatDate = (inputDate) => {
  const date = new Date(inputDate);

  const formattedDateIST = date.toLocaleDateString('en-IN', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  });
  return formattedDateIST;
};

const RenderDetails = ({ labelMappingArray, resolutionDetails }) => {
  return (
    <>
      {labelMappingArray.map((item, index) => {
        if (index === labelMappingArray.length - 1) {
          return (
            <DataRow
              dataLabel={item[1]}
              isDoc={true}
              docUrl={resolutionDetails[item[0]]}
              docName={item[1]}
            />
          );
        } else if (priceVariables[item[0]]) {
          return (
            <DataRow dataLabel={item[1]} dataVal={resolutionDetails[item[0]]} isPrice={true} />
          );
        } else if (dateVariables[item[0]]) {
          return <DataRow dataLabel={item[1]} dataVal={formatDate(resolutionDetails[item[0]])} />;
        } else if (item[0] === 'supplierDocumentType') {
          return (
            <DataRow dataLabel={item[1]} dataVal={supplierDocType[resolutionDetails[item[0]]]} />
          );
        }
        return <DataRow dataLabel={item[1]} dataVal={resolutionDetails[item[0]]} />;
      })}
    </>
  );
};

const ResolutionModal = ({ value, record }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [resolutionDetails, setResolutionDetails] = useState({});

  const handleInfoIconClick = async () => {
    const fabric = get_service_endpoint('fabric');
    const url = `${fabric}/orders/${record.orderId}/resolution-details`;
    const res = await axios.get(url, { isSession: true });
    setIsModalVisible(!isModalVisible);
    setResolutionDetails(res.data.data);
  };

  const customerCreditNoteAvailable = Boolean(
    resolutionDetails['customerCreditNoteNumber']?.length > 0,
  );
  const refundInitiated = Boolean(resolutionDetails['utrRefNumber']?.length > 0);
  const supplierDocAvailable = Boolean(resolutionDetails['supplierDocumentNumber']?.length > 0);
  const supplierDocLabelMapping = getSupplierDocLabelMapping(resolutionDetails);
  return (
    <>
      <div className="issue-details-container">
        <div className="issue-details-content">
          <p>
            <span className={record.resolutionDescription === 'Rejected' ? 'red-text' : 'green-text'}>
                {record.resolutionDescription === 'Rejected' ? 'Rejected' : 'Resolved'}
            </span>
            <br />
          </p>
        </div>

        <div className="issue-detail-info-icon-container">
          <img onClick={handleInfoIconClick} src={infoIcon} alt="info icon" />
        </div>
      </div>

      <Modal
        open={isModalVisible}
        onCancel={() => setIsModalVisible(!isModalVisible)}
        footer={null}
        className="issue-modal">
        <div className="modal-content scrollable-content">
          <h2 className="modal-title">{record.resolutionDescription}</h2>

          {customerCreditNoteAvailable ? (
            <>
              <DataRow dataLabel={'Credit note issued to the customer?'} dataVal={'Yes'} />
              <RenderDetails
                labelMappingArray={creditNoteLabelMapping}
                resolutionDetails={resolutionDetails}
              />
            </>
          ) : null}
          {refundInitiated ? (
            <>
              <DataRow dataLabel={'Refund initiated?'} dataVal={'Yes'} />
              <RenderDetails
                labelMappingArray={refundLabelMapping}
                resolutionDetails={resolutionDetails}
              />
            </>
          ) : null}
          {supplierDocAvailable ? (
            <>
              <DataRow dataLabel={'Supplier document available?'} dataVal={'Yes'} />
              <DataRow dataLabel={'Supplier name'} dataVal={resolutionDetails.supplierName} />
              <RenderDetails
                labelMappingArray={supplierDocLabelMapping}
                resolutionDetails={resolutionDetails}
              />
            </>
          ) : null}
          {resolutionDetails.isRejected ? (
            <DataRow dataLabel={'Rejection reason'} dataVal={resolutionDetails.rejectionReason} />
          ) : null}
        </div>
      </Modal>
    </>
  );
};
export default ResolutionModal;
